import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle, Edit, FileText, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { getOrganizationContracts } from "@/services/contracts/contractService";

// Define contract types for TypeScript
interface Contract {
  id: string;
  organization_id: string;
  type: 'msa' | 'sow';
  content: string;
  metadata: any;
  status: string;
  created_at: string;
  updated_at?: string;
  file_url?: string;
}

export default function EditContract() {
  const { id: contractId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  const [content, setContent] = useState("");
  const [metadata, setMetadata] = useState<any>({});
  const [status, setStatus] = useState<string>("draft");
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("content");
  
  // Fetch contract data
  const { data: contract, isLoading, error } = useQuery({
    queryKey: ["contract", contractId],
    queryFn: async () => {
      if (!contractId) throw new Error("Contract ID is required");
      
      const { data, error } = await supabase
        .from('generated_contracts')
        .select('*')
        .eq('id', contractId)
        .single();
        
      if (error) throw error;
      return data as Contract;
    },
    enabled: !!contractId,
    onSuccess: (data) => {
      setContent(data.content);
      setMetadata(data.metadata || {});
      setStatus(data.status);
    },
  });
  
  // Update contract mutation
  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<Contract>) => {
      if (!contractId) throw new Error("Contract ID is required");
      
      const { data, error } = await supabase
        .from('generated_contracts')
        .update(updates)
        .eq('id', contractId)
        .select()
        .single();
        
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contract", contractId] });
      queryClient.invalidateQueries({ queryKey: ["organization-contracts"] });
      toast({
        title: "Contract Updated",
        description: "Your changes have been saved successfully."
      });
      setIsEditing(false);
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update contract. Please try again.",
        variant: "destructive"
      });
    },
  });
  
  // Handle metadata field change
  const handleMetadataChange = (field: string, value: any) => {
    setMetadata(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Handle saving contract updates
  const handleSave = () => {
    updateMutation.mutate({
      content,
      metadata,
      status,
      updated_at: new Date().toISOString()
    });
  };
  
  // Generate PDF for contract
  const handleGeneratePDF = async () => {
    try {
      // Simulate PDF generation
      toast({
        title: "PDF Generation",
        description: "PDF generation is in progress..."
      });
      
      setTimeout(() => {
        toast({
          title: "PDF Generated",
          description: "Contract PDF has been generated successfully."
        });
      }, 2000);
    } catch (error: any) {
      toast({
        title: "PDF Generation Failed",
        description: error.message || "Failed to generate PDF. Please try again.",
        variant: "destructive"
      });
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  if (error || !contract) {
    return (
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle>Error Loading Contract</CardTitle>
          <CardDescription>
            The contract could not be loaded. It may have been deleted or you may not have permission to view it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="secondary" asChild>
            <Link to={`/organizations/${contract?.organization_id}`}>Return to Organization</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }
  
  const typeLabel = contract.type === 'msa' ? 'Master Services Agreement' : 'Statement of Work';

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/organizations/${contract.organization_id}`}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Edit {typeLabel}
          </h1>
          <p className="text-muted-foreground">
            Created on {new Date(contract.created_at).toLocaleDateString()}
          </p>
        </div>
        
        <div className="ml-auto flex space-x-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="pending_approval">Pending Approval</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="signed">Signed</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          
          <Button onClick={handleGeneratePDF} variant="outline" className="gap-2">
            <FileText className="h-4 w-4" />
            Generate PDF
          </Button>
          
          {isEditing ? (
            <Button onClick={handleSave} className="gap-2">
              {updateMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4" />
              )}
              Save Changes
            </Button>
          ) : (
            <Button onClick={() => setIsEditing(true)} className="gap-2">
              <Edit className="h-4 w-4" />
              Edit Contract
            </Button>
          )}
        </div>
      </div>
      
      <Card>
        <CardHeader className="border-b px-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 sm:w-[400px]">
              <TabsTrigger value="content">Contract Content</TabsTrigger>
              <TabsTrigger value="metadata">Metadata</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        
        <CardContent className="p-6">
          <TabsContent value="content">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">{typeLabel} Content</h3>
                {isEditing && (
                  <p className="text-sm text-muted-foreground">
                    Editing mode is enabled. Make your changes and click Save.
                  </p>
                )}
              </div>
              
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                disabled={!isEditing}
                className="min-h-[500px] font-mono text-sm"
              />
              
              {!isEditing && (
                <div className="flex justify-end">
                  <Button onClick={() => setIsEditing(true)} variant="outline">
                    Enable Editing
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="metadata">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Contract Metadata</h3>
                {isEditing && (
                  <p className="text-sm text-muted-foreground">
                    Edit the contract's metadata fields below.
                  </p>
                )}
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Organization Name</Label>
                    <Input 
                      value={metadata.organization_name || ''} 
                      onChange={(e) => handleMetadataChange('organization_name', e.target.value)}
                      disabled={!isEditing}
                    />
                  </div>
                  
                  <div>
                    <Label>Client Name</Label>
                    <Input 
                      value={metadata.client_name || ''} 
                      onChange={(e) => handleMetadataChange('client_name', e.target.value)}
                      disabled={!isEditing}
                    />
                  </div>
                  
                  <div>
                    <Label>Contract Value</Label>
                    <Input 
                      type="number" 
                      value={metadata.contract_value || ''} 
                      onChange={(e) => handleMetadataChange('contract_value', e.target.value)}
                      disabled={!isEditing}
                    />
                  </div>
                  
                  <div>
                    <Label>Start Date</Label>
                    <Input 
                      type="date" 
                      value={metadata.start_date || ''} 
                      onChange={(e) => handleMetadataChange('start_date', e.target.value)}
                      disabled={!isEditing}
                    />
                  </div>
                </div>
                
                <Separator />
                
                <div>
                  <Label>Project Name</Label>
                  <Input 
                    value={metadata.project_name || ''} 
                    onChange={(e) => handleMetadataChange('project_name', e.target.value)}
                    disabled={!isEditing}
                  />
                </div>
                
                <div>
                  <Label>Deliverables</Label>
                  <Textarea 
                    value={metadata.deliverables || ''} 
                    onChange={(e) => handleMetadataChange('deliverables', e.target.value)}
                    disabled={!isEditing}
                    className="min-h-[150px]"
                  />
                </div>
                
                <div>
                  <Label>Payment Schedule</Label>
                  <Textarea 
                    value={metadata.payment_schedule || ''} 
                    onChange={(e) => handleMetadataChange('payment_schedule', e.target.value)}
                    disabled={!isEditing}
                    className="min-h-[100px]"
                  />
                </div>
              </div>
              
              {!isEditing && (
                <div className="flex justify-end">
                  <Button onClick={() => setIsEditing(true)} variant="outline">
                    Enable Editing
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>
        </CardContent>
      </Card>
      
      {isEditing && (
        <div className="flex justify-end space-x-4">
          <Button variant="outline" onClick={() => setIsEditing(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </div>
      )}
    </div>
  );
}